import React, { useContext } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import Notes from 'app/src/components/Notes/List';
import CommissionDetails from 'app/src/components/Orders/CommissionDetails';
import ClientContext from 'app/src/contexts/ClientContext';
import DataState from 'app/src/elements/DataState';
import EditableDetails from 'app/src/elements/EditableDetails';
import {
  Text,
  Title,
  TitleLinks,
  Subtitle,
  Container,
  Additional,
} from 'app/src/styles';
import { cityStateZip } from 'app/src/utils/address';
import { Link, useParams } from 'app/src/utils/routing';

import EditDealer from './edit';
import DealerOrders from './Orders';
import Licenses from './Licenses';
import FeatureSubscriptions from './FeatureSubscriptions';
import Users from './Users';
import { DEALER, UPDATE_DEALER_INACTIVE } from './queries';

const Dealer = () => {
  const {
    currentUser, hasPolicy, isMobile, isTablet
  } = useContext(ClientContext);

  const { id } = useParams();
  const { t } = useTranslation();

  const { data, refetch, ...dataState } = useQuery(DEALER, {
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const [updateDealerInactive] = useMutation(UPDATE_DEALER_INACTIVE, {
    onCompleted: refetch
  });

  const activeLicenses = data ?
    data.dealer.licenses.filter(license => !license.inactiveAt) : [];

  const submitInactive = inactive => {
    const confirmation = inactive ?
      'Really make dealer inactive? It will no longer be shown in searches.' :
      'Really re-active dealer?';

    if (window.confirm(confirmation)) {
      updateDealerInactive({ variables: { id, inactive } });
    }
  };

  return (
    <View>
      <DataState data={data} {...dataState}>
        {data?.dealer && (
          <Container isTablet={isTablet}>
            <View style={isTablet ? {} : { flex: 1, maxWidth: 600 }}>
              <Title>
                {data?.dealer.name}
              </Title>
              <TitleLinks>

                {currentUser.isEmployee && (
                  data.dealer.inactiveAt ? (
                    <Pressable onPress={() => submitInactive(false)}>
                      <Text>⤇ INACTIVE - Reactivate</Text>
                    </Pressable>
                  ) : (
                    <Pressable onPress={() => submitInactive(true)}>
                      <Text>⤇ Make Inactive</Text>
                    </Pressable>
                  )
                )}

                <Link to={`/dealers/${id}/pre-registrations`}>
                  <Text>⤇ {t('titles.preRegistrationHistory')}</Text>
                </Link>

                <Link to={`/dealers/${id}/charts`}>
                  <Text>⤇ {t('titles.charts')}</Text>
                </Link>
              </TitleLinks>

              <EditableDetails
                modalContent={EditDealer}
                title={t('titles.updateDealer')}
                id={data.dealer.id}
                canEdit={hasPolicy({ type: 'Dealer', action: 'update' })}
                refetch={refetch}
              >
                <Text>{data.dealer.street}</Text>
                <Text>{cityStateZip(data.dealer)}</Text>
                <Text>{data.dealer.phone}</Text>
                <Text>{data.dealer.url}</Text>
                {data.dealer.userNickname && (
                  <Text>Your Nickname: {data.dealer.userNickname}</Text>
                )}
              </EditableDetails>

              <Subtitle>{t('titles.licenses')}</Subtitle>
              <EditableDetails
                modalContent={Licenses}
                title={t('titles.dealerLicenses')}
                id={data.dealer.id}
                canEdit={currentUser.isEmployee}
                refetch={refetch}
              >
                {activeLicenses.map(license => (
                  <View
                    key={license.id}
                    style={{ flexDirection: 'row', gap: 10 }}
                  >
                    <Text>{license.licenseType}</Text>
                    <Text>{license.licenseNumber}</Text>
                  </View>
                ))}
              </EditableDetails>

              <FeatureSubscriptions dealer={data?.dealer} refetch={refetch} />

              {currentUser?.isEmployee && (
                <View>
                  <Subtitle bordered>Users</Subtitle>
                  <Users dealer={data.dealer} refetch={refetch} />

                  <Subtitle bordered>Notes</Subtitle>
                  <Notes
                    notes={data.dealer.notes}
                    dealer={data.dealer}
                    refetch={refetch}
                  />
                </View>
              )}
            </View>

            <Additional isTablet={isTablet}>
              {currentUser?.isEmployee && (
                <CommissionDetails dealer={data.dealer} />
              )}

              <DealerOrders
                title={t('titles.pastOrders')}
                orders={data.dealer.orders}
                isMobile={isMobile}
              />
            </Additional>
          </Container>
        )}
      </DataState>
    </View>
  );
};

export default Dealer;
